// What a league row says about you, in the record book's voice.
//
// The row used to read "Season-Long · Created Feb 14, 2026". In week 3 the
// creation date is the least useful fact about a league, so it is the last
// thing here rather than the first — a fallback for a league that has not
// started, not the headline.
import { League } from '../types';

export interface StandingSummary {
  /** Segments for the row's secondary line, already ordered. */
  segments: string[];
  /** A matchup still in progress — the one thing here worth gold. */
  isLive: boolean;
}

/** 1st, 2nd, 3rd, 4th … 11th, 12th, 13th. */
export function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

/**
 * How this week is going, as a sentence.
 *
 * Deadpan and factual: you are leading or trailing, you beat them or lost to
 * them. The scoreline always reads from the caller's side.
 */
export function matchupLine(league: League): string | null {
  const m = league.my_matchup;
  if (!m) return null;

  const week = `Week ${m.week}`;
  if (!m.opponent_name) return `${week}: bye`;

  const mine = m.my_score;
  const theirs = m.opponent_score;
  const score = `${mine.toFixed(1)}–${theirs.toFixed(1)}`;

  if (m.is_completed) {
    if (mine > theirs) return `${week}: beat ${m.opponent_name} ${score}`;
    if (mine < theirs) return `${week}: lost to ${m.opponent_name} ${score}`;
    return `${week}: tied ${m.opponent_name} ${score}`;
  }

  // Nothing kicked yet reads as a fixture, not a 0–0 contest.
  if (mine === 0 && theirs === 0) return `${week}: vs ${m.opponent_name}`;
  if (mine > theirs) return `${week}: leading ${m.opponent_name} ${score}`;
  if (mine < theirs) return `${week}: trailing ${m.opponent_name} ${score}`;
  return `${week}: level with ${m.opponent_name} ${score}`;
}

/** Statuses that mean the league is actually being played right now. */
const IN_SEASON = ['active', 'drafting', 'playoffs'];

export function isInSeason(league: League): boolean {
  return IN_SEASON.includes(league.league_status || league.leagueStatus || '');
}

/**
 * Dashboard ordering: the leagues you are actually playing, first.
 *
 * The dashboard shows three of your leagues and used to pick them by creation
 * date, so a manager with a live 7-1 season could open the app and see three
 * finished leagues they only commission. Live state is worth nothing on rows
 * that have none.
 */
export function byRelevance(a: League, b: League): number {
  const score = (l: League) =>
    (l.my_team ? 4 : 0) +
    (standingSummary(l).isLive ? 2 : 0) +
    (isInSeason(l) ? 1 : 0);

  const diff = score(b) - score(a);
  if (diff !== 0) return diff;

  const at = Date.parse(a.created_at || a.createdAt || '') || 0;
  const bt = Date.parse(b.created_at || b.createdAt || '') || 0;
  return bt - at;
}

export function standingSummary(league: League): StandingSummary {
  const segments: string[] = [];
  const team = league.my_team;

  if (team) {
    segments.push(`${team.wins}–${team.losses}`);
    if (team.team_count > 1) {
      segments.push(`${ordinal(team.rank)} of ${team.team_count}`);
    }
  }

  const matchup = matchupLine(league);
  if (matchup) segments.push(matchup);

  // Nothing live to report — fall back to what the league *is*.
  if (segments.length === 0) {
    const type = league.league_type || league.leagueType;
    segments.push(type === 'weekly_redraft' ? 'Weekly Redraft' : 'Season-Long');
    const count = league.team_count;
    const max = league.max_teams || league.maxTeams;
    if (count != null && max) segments.push(`${count} of ${max} teams`);
  }

  const m = league.my_matchup;
  const isLive = Boolean(
    m && !m.is_completed && m.opponent_name &&
    (m.my_score > 0 || m.opponent_score > 0),
  );

  return { segments, isLive };
}
